<template>
  <!-- Anchored to the top of the canvas, just below the More button (which
       occupies y 5..63), rather than centred in it. The panel therefore
       starts at TOP_OFFSET and stays put; only the slide-in transform
       animates.

       FIXED positioning, spanning the whole page, so the panel can extend
       OVER the bottom sheet on short screens. It used to be absolute inside
       #mainContent, whose height is the canvas height (viewport minus the
       sheet) — on a short screen that left less room than the panel needs,
       so the sheet clipped it. Fixed positioning also lifts the drawer out
       of #mainContent's stacking context (z-index: 0), which is what let
       the sheet paint over it. -->
  <aside
    class="mobile-color-drawer fixed top-0 right-0 z-20 flex items-start pointer-events-none"
    :style="{
      height: '100%',
      paddingTop: `${TOP_OFFSET}px`,
    }"
    aria-label="Color palette drawer"
  >
    <!-- Drawer assembly: handle (left) + panel (right). Slides together as a unit. -->
    <div
      class="drawer-assembly relative flex items-center pointer-events-auto transition-transform duration-300 ease-out"
      :class="[isOpen ? 'translate-x-0' : 'translate-x-[91px]', drawerHeightClass]"
      :style="{ transition: 'transform 300ms ease-out' }"
    >
      <!-- Trigger Handle (Figma 7540:153007 — 18×99 navy arrow, sits OUTSIDE/left of panel) -->
      <button
        type="button"
        class="drawer-handle relative shrink-0 w-[18px] cursor-pointer focus:outline-none bg-transparent p-0 z-10"
        :class="handleHeightClass"
        :aria-expanded="isOpen"
        aria-label="Toggle color palette"
        @click="isOpen = !isOpen"
      >
        <!-- Navy arrow/pentagon shape (Figma Union 7520:165933, 18×99) -->
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="99"
          viewBox="0 0 18 99"
          fill="none"
          class="block"
        >
          <path
            d="M18 99C18 97.0396 10.2069 93.3696 7.19824 92.1386C7.00161 92.0582 6.80843 91.9754 6.61816 91.8907C6.57593 91.8719 6.53352 91.8532 6.4916 91.8342C6.3 91.7474 6.1121 91.6582 5.92734 91.5672C5.89998 91.5537 5.87369 91.5396 5.84648 91.526C5.67774 91.4418 5.51176 91.3561 5.34902 91.2685C5.29597 91.2399 5.24322 91.2113 5.19082 91.1824C5.04144 91.0999 4.89535 91.0158 4.75137 90.9306C4.71206 90.9073 4.67249 90.8842 4.63359 90.8607C4.47335 90.764 4.31566 90.666 4.1625 90.5659C4.11905 90.5375 4.07705 90.5084 4.03418 90.4797C3.71693 90.2675 3.41669 90.0476 3.13242 89.8212C3.1027 89.7975 3.07212 89.7742 3.04277 89.7503C2.90777 89.6408 2.77601 89.5299 2.64902 89.4172C2.61423 89.3864 2.57949 89.3555 2.54531 89.3244C1.98017 88.8103 1.49942 88.2657 1.1127 87.6961C1.08982 87.6625 1.06811 87.6285 1.0459 87.5947C0.976949 87.4896 0.911504 87.3836 0.849023 87.2769C0.829394 87.2433 0.80824 87.2101 0.789258 87.1764C0.717605 87.0492 0.65117 86.9209 0.588867 86.7916C0.574592 86.7619 0.560457 86.7323 0.54668 86.7025C0.48389 86.567 0.42496 86.4305 0.372656 86.2928C0.364816 86.2722 0.359164 86.2513 0.351562 86.2306C0.244743 85.9399 0.162162 85.6446 0.103711 85.3452C0.0982966 85.3175 0.0928793 85.2897 0.0878906 85.2619C0.063468 85.1258 0.0439761 84.9889 0.0298828 84.8513C0.0255125 84.8086 0.0209418 84.7658 0.0175781 84.723C0.00648744 84.5818 0 84.4398 0 84.297V14.703C0 14.5599 0.00643762 14.4176 0.0175781 14.276C0.0209494 14.2332 0.025505 14.1905 0.0298828 14.1478C0.0440004 14.0101 0.063444 13.8732 0.0878906 13.7371C0.0928842 13.7093 0.0982917 13.6816 0.103711 13.6539C0.162214 13.3545 0.244693 13.0591 0.351562 12.7684C0.359167 12.7477 0.364813 12.7268 0.372656 12.7062C0.424983 12.5685 0.483867 12.432 0.54668 12.2965C0.560462 12.2668 0.574588 12.2371 0.588867 12.2075C0.653082 12.0743 0.722153 11.9421 0.796289 11.8112C0.813168 11.7814 0.831637 11.7519 0.849023 11.7222C0.911521 11.6154 0.976933 11.5095 1.0459 11.4044C1.06811 11.3705 1.08982 11.3366 1.1127 11.3029C1.4995 10.7334 1.98011 10.1887 2.54531 9.67467C2.5795 9.64358 2.61423 9.61268 2.64902 9.58182C2.76273 9.48096 2.88044 9.38157 3.00059 9.28316L3.07441 9.22286C3.09325 9.20767 3.11343 9.19299 3.13242 9.17787C3.41671 8.95146 3.71692 8.73152 4.03418 8.5193C4.07705 8.49062 4.11905 8.46156 4.1625 8.43315C4.31566 8.33302 4.47335 8.23502 4.63359 8.13832C4.67249 8.11485 4.71206 8.09171 4.75137 8.06845C4.89535 7.98323 5.04144 7.89909 5.19082 7.8167C5.24322 7.7878 5.29597 7.75909 5.34902 7.73055C5.51176 7.64298 5.67774 7.55721 5.84648 7.47305C5.87369 7.45948 5.89999 7.44537 5.92734 7.43189C6.11209 7.34087 6.3 7.25166 6.4916 7.16483C6.53352 7.14583 6.57593 7.12714 6.61816 7.10835C6.80815 7.02382 7.00191 6.94171 7.19824 6.86139C10.2069 5.63043 18 1.9604 18 0V99Z"
            fill="#313558"
          />
          <!-- White chevron (Figma Vector 364 7520:165934, 5×24, centered at y=37.5) -->
          <g
            class="transition-transform duration-300 ease-out"
            :class="isOpen ? 'rotate-180' : 'rotate-0'"
            style="transform-origin: 9.5px 51px;"
          >
            <path
              d="M6.33325 1.5L1.65376 12.7308C1.44864 13.2231 1.44864 13.7769 1.65376 14.2692L6.33325 25.5"
              stroke="white"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              transform="translate(5.5 37.5)"
            />
          </g>
        </svg>
      </button>

      <!-- Drawer Content Panel (Figma: 91×355, 18.46px rounded left corners) -->
      <div
        class="drawer-panel w-[91px] h-full overflow-y-auto bg-white flex flex-col pl-2 pr-1 shadow-[-7px_0_13px_rgba(20,20,22,0.1)] rounded-l-[18px]"
        :class="isSmallDrawer ? 'py-6' : 'py-2.5'"
      >
        <!-- Recently Used row: last 4 colors the user picked. Hidden when the
           drawer is in the small (printed pattern) layout to keep Figma
           7540:153007's 91×135 panel pixel-faithful. -->
        <div
          v-if="showRecentColors"
          class="recent-colors flex flex-col items-center"
        >
          <p class="text-[9px] font-bold text-[#716D73] uppercase tracking-wider w-full text-left mb-1">
            Recent
          </p>
          <div class="grid grid-cols-2 gap-x-1 gap-y-1 justify-items-center">
            <button
              v-for="color in recentColors"
              :key="`recent-${color.label}`"
              type="button"
              class="recent-swatch relative w-[34px] h-[34px] rounded-full transition-transform active:scale-95 focus:outline-none"
              :class="{ 'is-active': activeColorLabel === color.label }"
              :style="getSwatchStyle(color)"
              :title="color.label"
              :aria-label="`Recent: ${color.label}`"
              :aria-pressed="activeColorLabel === color.label"
              @click="selectColor(color, $event)"
            >
              <span
                v-if="activeColorLabel === color.label"
                class="active-ring absolute inset-0 rounded-full border-2 border-[#0C0E12]"
              />
            </button>
          </div>
          <!-- Divider between Recent row and the rest of the drawer body -->
          <div class="border-t border-[#EEEEEA] w-full mt-2 mb-2" />
        </div>

        <!-- Top/Bottom layer buttons (Figma 7154:232015) — only when layered engraved is selected -->
        <div
          v-if="isLayered"
          class="layer-buttons flex flex-col gap-1.5 pb-2"
          role="group"
          aria-label="Felt layer colors"
        >
          <button
            type="button"
            class="layer-btn flex items-center gap-1.5 w-full h-[34px] px-2 rounded-md border transition-colors text-left"
            :class="gridStore.activeLayer === 'top'
              ? 'border-[#313558] bg-white'
              : 'border-[#E6E5E0] bg-[#FAFAFA] hover:border-[#CCC]'"
            :aria-pressed="gridStore.activeLayer === 'top'"
            @click="setLayer('top')"
          >
            <span
              class="layer-swatch w-[18px] h-[18px] rounded-full shrink-0 border border-black/5"
              :style="getSwatchStyle(topColor)"
            />
            <span class="flex flex-col leading-tight min-w-0">
              <span class="text-[9px] font-bold text-[#716D73] tracking-wide uppercase">Top</span>
              <span class="text-[11px] font-semibold text-[#202124] truncate">{{ topColor.label }}</span>
            </span>
          </button>
          <button
            type="button"
            class="layer-btn flex items-center gap-1.5 w-full h-[34px] px-2 rounded-md border transition-colors text-left"
            :class="gridStore.activeLayer === 'bottom'
              ? 'border-[#313558] bg-white'
              : 'border-[#E6E5E0] bg-[#FAFAFA] hover:border-[#CCC]'"
            :aria-pressed="gridStore.activeLayer === 'bottom'"
            @click="setLayer('bottom')"
          >
            <span
              class="layer-swatch w-[18px] h-[18px] rounded-full shrink-0 border border-black/5"
              :style="getSwatchStyle(bottomColor)"
            />
            <span class="flex flex-col leading-tight min-w-0">
              <span class="text-[9px] font-bold text-[#716D73] tracking-wide uppercase">Bottom</span>
              <span class="text-[11px] font-semibold text-[#202124] truncate">{{ bottomColor.label }}</span>
            </span>
          </button>
        </div>

        <!-- Divider when layered -->
        <div v-if="isLayered" class="border-t border-[#EEEEEA] mb-2" />

        <!-- Swatches 2-Column Grid -->
        <div
          :key="swatchGridKey"
          class="grid grid-cols-2 gap-x-1 gap-y-[5px] justify-items-center"
        >
          <button
            v-for="color in colors"
            :key="color.label"
            type="button"
            class="swatch-circle relative w-[38px] h-[38px] rounded-full transition-transform active:scale-95 focus:outline-none"
            :class="{ 'is-active': activeColorLabel === color.label }"
            :style="getSwatchStyle(color)"
            :title="color.label"
            :aria-label="color.label"
            :aria-pressed="activeColorLabel === color.label"
            @click="selectColor(color, $event)"
          >
            <span
              v-if="activeColorLabel === color.label"
              class="active-ring absolute inset-0 rounded-full border-2 border-[#0C0E12]"
            />
          </button>
        </div>

        <!-- Wood Grain row: its own section, mirroring the desktop
             ColorPicker. These colours only apply to a BLANK 3D box on the
             3D grid, so they are kept out of the main swatch grid and shown
             here instead. -->
        <div v-if="woodGrainColors.length" class="wood-grain-colors flex flex-col items-center mt-3">
          <p class="text-[9px] font-bold text-[#716D73] uppercase tracking-wider w-full text-left mb-1">
            Wood Grain
          </p>
          <div class="grid grid-cols-2 gap-x-1 gap-y-1 justify-items-center">
            <button
              v-for="swatch in woodGrainColors"
              :key="swatch.label"
              type="button"
              class="swatch-circle relative w-[38px] h-[38px] rounded-full transition-transform active:scale-95 focus:outline-none"
              :class="{ 'is-active': activeColorLabel === swatch.label }"
              :style="getSwatchStyle(swatch)"
              :title="swatch.label"
              :aria-label="`Wood Grain: ${swatch.label}`"
              :aria-pressed="activeColorLabel === swatch.label"
              @click="selectColor(swatch, $event)"
            >
              <span
                v-if="activeColorLabel === swatch.label"
                class="active-ring absolute inset-0 rounded-full border-2 border-[#0C0E12]"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { isLayeredShape } from '~/shared/layeredEngraving'
import feltColors from '~/server/utils/threeDDesignSync/felt-colors.json'
import { createPickerInteractions } from '~/components/NewStudio/logic'
import { loadVariations, getColorVariation } from '~/composables/shapes/utils'
import { getColorsForPattern } from '~/composables/shapes/printedPatterns'
import { isBlank3DBox } from '~/composables/shapes/shapesFor3D'
import { getWoodSlatFinishChoices, isWoodSlatShapeId } from '~/composables/shapes/woodSlats'
import { isWoodGrainColor } from '~/composables/shapes/colors'
import { useRecentColorsStore } from '~/stores/recentColors'
import type { ColorChoice } from '~/composables/shapes/colors'

const gridStore = useGridStore()
const recentColorsStore = useRecentColorsStore()
const { getColors, fetchColors } = useStudioColors()
const { getColorFlags, fetchConfig } = useStudioConfig()
const userStore = useUserStore()
const { getSwatchStyle } = useColorSwatch()
const interactions = useShapeInteractions()
const picker = createPickerInteractions(interactions)

const isOpen = ref(false)

// Gap between the top of the canvas and the drawer's panel. The More button
// occupies roughly y 5..63, so 72px clears it with a little breathing room and
// matches the panel's top edge in Figma 7671:240258.
const TOP_OFFSET = 72

// Hydrate the recents store from localStorage on mount. Safe to call
// multiple times — `hydrate` is idempotent until it has run once.
onMounted(() => {
  recentColorsStore.hydrate()
})

// Reactive trigger that flips when variations finish loading, so the colors
// ref re-evaluates and can apply the pattern allowlist filter.
const variationsReady = ref(false)

// Load variations, studio colors and the studio config before filtering can
// run. The config carries the per-colour admin/active flags the desktop
// picker uses to hide colours a given user must not see.
onMounted(async () => {
  try {
    await Promise.all([loadVariations(), fetchColors(), fetchConfig()])
  } catch (e) {
    // ignore — fallback to all colors if anything can't load
  }
  variationsReady.value = true
})

// Filtered colors ref. We use a watchEffect + explicit ref rather than a
// computed because:
//   1. The filter needs variations catalog + studio colors to be loaded
//      before it can filter (variationsReady gates this).
//   2. `getColors()` rebuilds the array each call (via withWoodSlatFinishes),
//      so a computed returns a new reference every render — Vue's keyed
//      diff sees a new list and may keep stale DOM nodes when transitioning
//      from "no shape" (66 colors) to "Geo printed" (4 colors). Using a
//      ref + explicit watch effect ensures the array reference only flips
//      when the actual filtered contents change.
//   3. We rebuild the array from scratch each update so Vue's keyed v-for
//      cleanly removes dropped swatches.
//
// The rules mirror the desktop ColorPicker (components/NewStudio/ColorPicker.vue):
//   - Wood grain colours (Natural Walnut, Medium Elm, …) are NOT in the
//     main swatch grid; they live in their own row, available only on a
//     BLANK 3D box on the 3D grid.
//   - Wood slat finishes ("Oak / Black", …) are not in the main grid either;
//     a slat shows only its own finishes.
//   - The shape's own allowedColors wins; otherwise the printed pattern's
//     variation allowlist applies.
const colors = ref<ColorChoice[]>([])
const woodGrainColors = ref<ColorChoice[]>([])
watchEffect(() => {
  // Explicit reactive dependencies — read them first so Vue tracks them.
  const ready = variationsReady.value
  const selectedShape = gridStore.selectedShape
  const selectedPrefix = selectedShape?.prefix
  const selectedColorLabel = gridStore.selectedColor?.label
  // Touch selectedColorLabel so this effect re-runs when the user picks a
  // different color (the swatch list itself doesn't change, but downstream
  // is-active states do).
  void selectedColorLabel

  const allColors = getColors()
  const isWoodSlat = isWoodSlatShapeId(selectedShape?.id)

  // Per-colour admin/active flags, exactly as the desktop picker applies
  // them: a colour can be hidden for everyone (inactive) or for non-admins.
  const isVisibleColor = (c: ColorChoice) => {
    const flags = getColorFlags(c.label, c.admin, c.active)
    return flags.active && (!flags.admin || userStore.isAdmin)
  }

  // The base grid excludes wood grain and wood slat finishes. Both have their
  // own places in the picker, so showing them mixed into the felt swatches
  // offered shapes that cannot use them.
  const baseColors = (isWoodSlat
    ? getWoodSlatFinishChoices(selectedShape?.id)
    : allColors.filter(c => !isWoodGrainColor(c.label) && !c.label.includes(' / ')))
    .filter(isVisibleColor)

  // Wood grain is only available on a BLANK 3D box (not engraved variants),
  // and only on the 3D grid.
  const showWoodGrain = gridStore.gridPattern?.id === '3d'
    && isBlank3DBox(selectedPrefix)
  const wood = showWoodGrain
    ? allColors.filter(c => isWoodGrainColor(c.label)).filter(isVisibleColor)
    : []

  // Until variations load, show the full list so the UI is never empty.
  if (!ready || !selectedPrefix) {
    colors.value = baseColors
    woodGrainColors.value = wood
    return
  }

  // The shape's own allowedColors wins (wood slats carry their finishes
  // there). Otherwise fall back to the printed pattern's variation allowlist:
  // each printed shape (Aspen, Bamboo, Geo, etc.) has exactly 4 color
  // variations (per Figma 7540:153007), and showing the others would let
  // users select a color that won't render on the tile.
  const shapeAllowlist = selectedShape?.allowedColors
  const allowlist = shapeAllowlist?.length
    ? shapeAllowlist
    : getColorsForPattern(selectedPrefix, baseColors)

  const filtered = allowlist && allowlist.length > 0
    ? baseColors.filter(c => allowlist.includes(c.label))
    : baseColors

  // Only assign a new reference when the contents actually changed, so
  // Vue's v-for can reconcile by key without churning the DOM.
  if (
    filtered.length !== colors.value.length
    || filtered.some((c, i) => c.label !== colors.value[i]?.label)
  ) {
    colors.value = filtered
  }
  if (
    wood.length !== woodGrainColors.value.length
    || wood.some((c, i) => c.label !== woodGrainColors.value[i]?.label)
  ) {
    woodGrainColors.value = wood
  }
})

// Key on the swatch grid so Vue tears down the whole v-for when the filtered
// color list shrinks (e.g. switching from "all colors" to "Geo only"). Without
// this, dev-mode hydration can leave stale swatches in the DOM.
const swatchGridKey = computed(() => {
  const prefix = gridStore.selectedShape?.prefix || 'none'
  const len = colors.value.length
  return `${prefix}:${len}`
})

const isLayered = computed(() => isLayeredShape(gridStore.selectedShape))

// "Recently Used" — last 4 colors the user picked, unfiltered by pattern.
// Resolution and deactivation filtering happens in the store's getter.
const recentColors = computed(() => recentColorsStore.recentColorChoices)
// Only show the Recent row on the full palette drawer. The 91×135 printed
// pattern layout is pixel-faithful to Figma 7540:153007 and doesn't have
// room; also, picking a recent color that's not in the printed pattern's
// allowlist would produce a fallback warning.
const showRecentColors = computed(() => !isSmallDrawer.value && recentColors.value.length > 0)

// Adaptive drawer size matching the Figma specs:
//   - Figma 7540:153007 (small / printed patterns): 91×135 panel, 18×99 handle
//   - Figma 7540:150291 (large / many colors):    91×355 panel, 18×117 handle
//   - Layered engraved (small + 2 layer buttons): panel taller to fit the
//     layer section above the swatch grid.
const SMALL_DRAWER_THRESHOLD = 4
const isSmallDrawer = computed(() => colors.value.length > 0 && colors.value.length <= SMALL_DRAWER_THRESHOLD)
// Tailwind only sees static class strings, so list every height we may need.
// 135px = small non-layered (matches Figma 7540:153007 panel exactly).
// 245px = small layered (135 + 110 extra for layer buttons + divider).
// 355px = full-size drawer (matches Figma 7540:150291).
const drawerHeightClass = computed(() => {
  if (colors.value.length > SMALL_DRAWER_THRESHOLD) return 'h-[355px]'
  return isLayered.value ? 'h-[245px]' : 'h-[135px]'
})
// 99px = small handle (matches Figma 7540:153007 Union 18×99).
// 117px = large handle (matches Figma 7540:150368 Union 18×117).
const handleHeightClass = computed(() => isSmallDrawer.value ? 'h-[99px]' : 'h-[117px]')

// Bottom color: prefer store override, otherwise derive from the selected shape+color
const bottomColor = computed<ColorChoice>(() => {
  if (gridStore.layeredBottomColor) return gridStore.layeredBottomColor
  const config = gridStore.selectedShape && gridStore.selectedColor
    ? getColorVariation(gridStore.selectedShape, gridStore.selectedColor, { allowFallback: false })?.layered_engraving
    : undefined
  const label = config?.default_bottom_color || 'Ivory'
  return getColors().find(c => c.label === label) || { label, hex: config?.default_bottom_hex || feltColors.Ivory, image: '' } as ColorChoice
})

// Top color falls back to a neutral swatch if nothing is selected yet
const topColor = computed<ColorChoice>(() => gridStore.selectedColor || { label: '—', hex: '#CCCCCC', image: '' } as ColorChoice)

const activeColorLabel = computed(() => {
  if (isLayered.value && gridStore.activeLayer === 'bottom') {
    return bottomColor.value.label
  }
  return gridStore.selectedColor?.label
})

const selectColor = (color: ColorChoice, event: MouseEvent) => {
  picker.selectColor(color as any, event)
}

const setLayer = (layer: 'top' | 'bottom') => {
  gridStore.activeLayer = layer
}

defineExpose({
  open: () => { isOpen.value = true },
  close: () => { isOpen.value = false },
  toggle: () => { isOpen.value = !isOpen.value }
})
</script>

<style scoped>
.swatch-circle,
.recent-swatch {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
}

.swatch-circle.is-active,
.recent-swatch.is-active {
  transform: scale(1.05);
}

.drawer-panel {
  scrollbar-width: thin;
  scrollbar-color: #E6E5E0 transparent;
}

.drawer-panel::-webkit-scrollbar {
  width: 4px;
}

.drawer-panel::-webkit-scrollbar-thumb {
  background: #E6E5E0;
  border-radius: 4px;
}

.layer-swatch {
  background-position: center;
  background-repeat: no-repeat;
  background-size: 125%;
}
</style>
